import type { VideoCodec } from "./VideoCodec";

/**
 * Minimal VP9 bitstream inspection.
 *
 * VP9 has no Annex B framing: a message payload is one raw frame (possibly a
 * superframe, whose index trails the payload — so byte 0 always starts the
 * first frame). This module parses only the fixed-position leading bits of
 * the uncompressed frame header (VP9 Bitstream Specification section 6.2):
 * enough for keyframe detection and the `vp09.*` WebCodecs codec string. The
 * decoder reads everything else from the stream.
 */

const FRAME_MARKER = 0b10;
const SYNC_CODE = 0x498342;

// The header bits read here span at most 10 bits, followed by the 24-bit
// sync code — under 5 bytes. Real frames are orders of magnitude larger;
// anything shorter cannot be a decodable frame.
const MIN_HEADER_BYTES = 5;

interface KeyframeHeader {
  profile: number;
  /** 8, 10, or 12 — from the color config (profiles 2/3 carry a depth bit). */
  bitDepth: number;
}

/**
 * Parse the uncompressed header far enough to identify a keyframe. Returns
 * `undefined` for non-keyframes, `show_existing_frame` payloads, and data
 * that fails the frame-marker or sync-code checks.
 */
function parseKeyframeHeader(data: Uint8Array): KeyframeHeader | undefined {
  if (data.length < MIN_HEADER_BYTES) {
    return undefined;
  }
  let position = 0;
  const read = (count: number): number => {
    let value = 0;
    for (let i = 0; i < count; i++) {
      value =
        (value << 1) | ((data[position >> 3] >> (7 - (position % 8))) & 1);
      position += 1;
    }
    return value;
  };

  if (read(2) !== FRAME_MARKER) {
    return undefined;
  }
  const profile = read(1) | (read(1) << 1);
  if (profile === 3) {
    read(1); // reserved_zero
  }
  if (read(1) === 1) {
    return undefined; // show_existing_frame: re-display, not a new frame
  }
  if (read(1) !== 0) {
    return undefined; // frame_type: 0 = KEY_FRAME
  }
  read(2); // show_frame, error_resilient_mode
  if (read(24) !== SYNC_CODE) {
    return undefined; // arbitrary bytes that happened to match the flag bits
  }
  // color_config: profiles 2/3 declare 10- or 12-bit; 0/1 are always 8-bit.
  if (profile < 2) {
    return { profile, bitDepth: 8 };
  }
  return { profile, bitDepth: read(1) === 1 ? 12 : 10 };
}

/**
 * Whether the VP9 frame is a keyframe (intra-only, clean decoder entry
 * point). In WebCodecs terms, such a frame is an `EncodedVideoChunk` of type
 * "key"; all other frames are "delta".
 */
export function isKeyframe(data: Uint8Array): boolean {
  return parseKeyframeHeader(data) !== undefined;
}

/**
 * Derive the WebCodecs codec string (`vp09.PP.LL.DD`, VP Codec ISO Media
 * binding) for the frame, or `undefined` when it is not a keyframe.
 *
 * Profile and bit depth come from the keyframe header. The VP9 bitstream
 * carries no level (it is container-level metadata), so level 1.0 is
 * declared: WebCodecs uses the level only as a capability hint, and the
 * conservative value never causes a spurious "unsupported codec" rejection.
 */
export function codecStringFromKeyframe(data: Uint8Array): string | undefined {
  const header = parseKeyframeHeader(data);
  if (header === undefined) {
    return undefined;
  }
  const profile = String(header.profile).padStart(2, "0");
  const bitDepth = String(header.bitDepth).padStart(2, "0");
  return `vp09.${profile}.10.${bitDepth}`;
}

/**
 * The VP9 implementation of the {@link VideoCodec} seam: raw frames (no
 * Annex B), keyframes identified by the uncompressed frame header, and a
 * `vp09.*` codec string from the keyframe's profile and bit depth. Profile 0
 * 8-bit (`vp09.00.10.08`) is the capability probe because it is the most
 * broadly supported configuration.
 */
export const vp9: VideoCodec = {
  formats: ["vp9"],
  framing: "raw",
  probeCodecString: "vp09.00.10.08",
  isKeyframe,
  codecStringFromKeyframe,
};
